import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  SsoConnectionDto,
  SsoConnectionsCreateRequestDto,
  SsoConnectionsReplaceDomainsRequestDto,
  SsoConnectionsReplaceRoleMappingsRequestDto,
  SsoConnectionsUpdateRequestDto
} from './vive-api-models';

// ADMIN_ADMIN only, because a role mapping can grant up to ADMIN_ADMIN. Every
// route answers with the whole connection, domains and role mappings included.
class SsoConnectionsService extends ViveApiBaseService {
  // There is no get-by-id: read one connection from this list.
  public async findAll(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<SsoConnectionDto[]> {
    const { options } = params ?? {};
    return await this.sendGet<SsoConnectionDto[]>({
      apiEndpoint: 'admin/sso-connections',
      options
    });
  }

  // Always created as a draft. Protocol, scope and CompanyId cannot change later.
  public async create(params: {
    options?: ViveApiRequestOptions;
    data: SsoConnectionsCreateRequestDto;
  }): Promise<SsoConnectionDto> {
    const { options, data } = params;
    return await this.sendPost<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections',
      options,
      data
    });
  }

  public async updateById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { connectionId: number };
    data: SsoConnectionsUpdateRequestDto;
  }): Promise<SsoConnectionDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections/:connectionId',
      options,
      pathParams,
      data
    });
  }

  // Replaces the whole set: send every domain the connection should keep.
  public async replaceDomains(params: {
    options?: ViveApiRequestOptions;
    pathParams: { connectionId: number };
    data: SsoConnectionsReplaceDomainsRequestDto;
  }): Promise<SsoConnectionDto> {
    const { options, pathParams, data } = params;
    return await this.sendPut<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections/:connectionId/domains',
      options,
      pathParams,
      data
    });
  }

  // Replaces the whole set: send every mapping the connection should keep.
  public async replaceRoleMappings(params: {
    options?: ViveApiRequestOptions;
    pathParams: { connectionId: number };
    data: SsoConnectionsReplaceRoleMappingsRequestDto;
  }): Promise<SsoConnectionDto> {
    const { options, pathParams, data } = params;
    return await this.sendPut<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections/:connectionId/role-mappings',
      options,
      pathParams,
      data
    });
  }

  // Checks the protocol fields only, not domains or mappings.
  public async activate(params: {
    options?: ViveApiRequestOptions;
    pathParams: { connectionId: number };
  }): Promise<SsoConnectionDto> {
    const { options, pathParams } = params;
    return await this.sendPost<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections/:connectionId/activate',
      options,
      pathParams
    });
  }

  // Signs out everyone who signed in through the connection. Its domains stay
  // held, so another connection cannot take them until they are replaced.
  public async disable(params: {
    options?: ViveApiRequestOptions;
    pathParams: { connectionId: number };
  }): Promise<SsoConnectionDto> {
    const { options, pathParams } = params;
    return await this.sendPost<SsoConnectionDto>({
      apiEndpoint: 'admin/sso-connections/:connectionId/disable',
      options,
      pathParams
    });
  }
}

export const ssoConnectionsService = new SsoConnectionsService();
